import { cn } from '../../utils/cn';
import type { ReportSummaryItem, ReportSummaryTone } from './types';

/**
 * The figures a report is read for, as ONE strip.
 *
 * Deliberately not a grid of metric tiles. Four identical cards with a big number in each is the
 * SaaS cliché the design bans, and it also reads wrong here: these figures are one sentence about
 * one filter, not four independent facts. A single bordered container with hairline dividers says
 * that, and it survives the wrap to two rows on a narrow screen without turning into a card wall.
 *
 * Tone colours the VALUE only. The label stays neutral so a strip with one warning figure has one
 * coloured thing in it, not two.
 */

const TONE_CLASS: Record<ReportSummaryTone, string> = {
  default: 'text-foreground',
  positive: 'text-success-strong',
  warning: 'text-warning-strong',
  critical: 'text-destructive-strong',
};

export interface ReportSummaryStripProps {
  items: ReportSummaryItem[];
  className?: string;
  testId?: string;
  /** Accessible name, e.g. the report's title. */
  ariaLabel?: string;
}

export function ReportSummaryStrip({
  items,
  className,
  testId,
  ariaLabel,
}: ReportSummaryStripProps) {
  if (items.length === 0) return null;

  return (
    <div
      role="group"
      aria-label={ariaLabel}
      data-testid={testId}
      className={cn(
        'flex flex-wrap rounded-lg border border-border bg-card',
        'divide-x divide-border rtl:divide-x-reverse',
        className,
      )}
    >
      {items.map((item) => {
        const Icon = item.icon;
        return (
          <div
            key={item.label}
            className="min-w-[9rem] flex-1 px-4 py-3"
            data-testid="report-summary-item"
          >
            {/* The per-item name is on an inner wrapper, not on the cell: every item must keep the
                shared `report-summary-item` id so a caller can still count or sweep the strip. */}
            <div data-testid={item.testId}>
              <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                {Icon && <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />}
                <span className="truncate">{item.label}</span>
              </div>
              <div
                className={cn(
                  'mt-1 text-xl font-semibold tabular-nums',
                  TONE_CLASS[item.tone ?? 'default'],
                )}
              >
                {item.value}
              </div>
              {item.hint && <p className="mt-0.5 text-xs text-muted-foreground">{item.hint}</p>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default ReportSummaryStrip;
